import { defineComponent, h, ref } from 'vue';
import { InspectionReportExport } from '@/ui/pages/inspection/export/InspectionReportExport';
import {
  checkReportExportPhotosExist,
  inspectionsReportExportPhotosUrl,
  inspectionsReportExportUrl,
  SupportedExportFormat, SupportedExportFormatEnum,
} from '@/services/api/resources/inspection/exports';
import { download, toasted } from '@/ui/helpers';
import { t } from '@/ui/i18n/i18n';
import { InspectionRecord } from '@/entities/inspection';
import { syncRefWithTaskState, useTask } from '@/ui/concerns';
import { user } from '@/ui/auth/auth';
import { Right } from 'fp-ts/lib/Either';
import { StatusCodes } from 'http-status-codes';
import { useInspectionExport } from '@/ui/pages/inspection/useInspectionExport';

interface InspectionIndexExportProps {
  selectedResources: InspectionRecord[];
  formats: Array<SupportedExportFormatEnum>;
}

export const InspectionIndexExport = defineComponent<InspectionIndexExportProps>({
  name: 'InspectionIndexExport',
  props: ['selectedResources', 'formats'],
  setup(props, context) {
    const loading = ref(false);

    const { saveFileFromUrl } = useInspectionExport();

    const exportReports = (format: SupportedExportFormat) => {
      const { run } = useTask(
        () => inspectionsReportExportUrl(
          props.selectedResources.map(resource => resource.data.inspection.id.toString()),
          format,
          user.value.timezone,
        ),
        url => {
          const task = saveFileFromUrl(url.href);
          syncRefWithTaskState(loading, task);
          context.emit('exported');
        },
      );

      return run;
    };

    const exportReportsPhotos = (format: SupportedExportFormat) => {
      const { run } = useTask(
        () => inspectionsReportExportPhotosUrl(
          props.selectedResources.map(resource => resource.data.inspection.id.toString()),
          format,
          user.value.timezone,
        ),
        async url => {
          const { right } = await checkReportExportPhotosExist(url.href)() as Right<Response>;

          if (right.status === StatusCodes.NOT_FOUND) {
            toasted.error(t('inspections.no-photos-to-be-exported'));

            return;
          }

          download(url);
          context.emit('exported');
        },
      );

      return run;
    };

    const onExportClick = ({ format }: {format: SupportedExportFormat | 'photos'}) => {
      if (props.selectedResources.length < 1) {
        toasted.error(t('report-download.select-item'));

        return;
      }

      if (props.selectedResources.length > 10) {
        toasted.error(t('report-download.maximum-number-items-selected', { number: '10' }));

        return;
      }

      if (format === 'photos') {
        exportReportsPhotos('batch-export-photos')();
      } else {
        exportReports(format as SupportedExportFormat)();
      }
    };

    return function render() {
      return h(InspectionReportExport, {
        props: {
          hasPhotos: true,
          hasPdf: true,
          loading: loading.value,
          iconized: true,
          tooltiped: true,
          formats: props.formats,
        },
        on: {
          export: onExportClick,
        },
      });
    };
  },
});

export const inspectionIndexExport = (
  { props, on }: { props: InspectionIndexExportProps; on?: Record<string, Function> },
) => h(InspectionIndexExport, { props, on });
